/**
 * Compute a hierarchical layout for the family tree.
 * Returns { nodes: {id -> {x, y, gen, ...person}}, edges: [{from, to, type}], size: {w, h}}
 *
 * Simple algorithm:
 *  - Generation via BFS from roots (persons with no incoming 'parent' edge).
 *  - Order within a generation: DFS from roots so children fall under their parents.
 *  - Keep spouses adjacent within a row.
 */
const NODE_W = 200;
const NODE_H = 108;
const COL_GAP = 56;
const ROW_GAP = 120;
const PAD = 80;

export function layoutTree(persons, relationships) {
  const byId = new Map();
  persons.forEach((p) => byId.set(p.id, { ...p }));

  const parentsOf = new Map(); // childId -> [parentIds]
  const childrenOf = new Map(); // parentId -> [childIds]
  const spouseOf = new Map(); // id -> Set(spouseIds)

  for (const r of relationships) {
    if (r.type === "parent") {
      if (!parentsOf.has(r.to_person_id)) parentsOf.set(r.to_person_id, []);
      parentsOf.get(r.to_person_id).push(r.from_person_id);
      if (!childrenOf.has(r.from_person_id)) childrenOf.set(r.from_person_id, []);
      childrenOf.get(r.from_person_id).push(r.to_person_id);
    } else if (r.type === "spouse") {
      if (!spouseOf.has(r.from_person_id)) spouseOf.set(r.from_person_id, new Set());
      if (!spouseOf.has(r.to_person_id)) spouseOf.set(r.to_person_id, new Set());
      spouseOf.get(r.from_person_id).add(r.to_person_id);
      spouseOf.get(r.to_person_id).add(r.from_person_id);
    }
  }

  // Assign generation via BFS from every root; iterate until stable.
  const gen = new Map();
  const roots = [];
  for (const p of persons) {
    if (!parentsOf.has(p.id) || parentsOf.get(p.id).length === 0) {
      roots.push(p.id);
      gen.set(p.id, 0);
    }
  }
  // iterate to propagate — max parent gen + 1
  let changed = true;
  let iter = 0;
  while (changed && iter < 200) {
    changed = false;
    iter += 1;
    for (const p of persons) {
      const parents = parentsOf.get(p.id) || [];
      if (parents.length === 0) continue;
      const maxParent = Math.max(...parents.map((pid) => (gen.has(pid) ? gen.get(pid) : -Infinity)));
      if (Number.isFinite(maxParent)) {
        const target = maxParent + 1;
        if (gen.get(p.id) !== target) {
          gen.set(p.id, target);
          changed = true;
        }
      }
    }
    // Align spouses to the same row (max of the two)
    for (const [id, spouses] of spouseOf.entries()) {
      if (!gen.has(id)) continue;
      for (const sid of spouses) {
        const a = gen.get(id);
        const b = gen.get(sid);
        const m = Math.max(a ?? -Infinity, b ?? -Infinity);
        if (Number.isFinite(m)) {
          if (gen.get(id) !== m) {
            gen.set(id, m);
            changed = true;
          }
          if (gen.get(sid) !== m) {
            gen.set(sid, m);
            changed = true;
          }
        }
      }
    }
  }
  // Any remaining unplaced (shouldn't happen but safety)
  for (const p of persons) if (!gen.has(p.id)) gen.set(p.id, 0);

  // Bucket by generation
  const rows = new Map();
  for (const p of persons) {
    const g = gen.get(p.id);
    if (!rows.has(g)) rows.set(g, []);
    rows.get(g).push(p.id);
  }

  // Order each row: DFS through roots, place each person then spouse right after, then descend to children.
  const orderedRows = new Map();
  const placed = new Set();

  function placeInRow(id) {
    const g = gen.get(id);
    if (!orderedRows.has(g)) orderedRows.set(g, []);
    if (placed.has(id)) return;
    orderedRows.get(g).push(id);
    placed.add(id);
    // place spouses right next to
    const sps = spouseOf.get(id);
    if (sps) {
      for (const sid of sps) {
        if (!placed.has(sid) && gen.get(sid) === g) {
          orderedRows.get(g).push(sid);
          placed.add(sid);
        }
      }
    }
  }
  function dfs(id) {
    placeInRow(id);
    // then children
    const kids = childrenOf.get(id) || [];
    for (const kid of kids) dfs(kid);
    // spouse's children too
    const sps = spouseOf.get(id);
    if (sps) {
      for (const sid of sps) {
        const skids = childrenOf.get(sid) || [];
        for (const kid of skids) dfs(kid);
      }
    }
  }
  for (const r of roots) dfs(r);
  // pick up any orphans not reachable from a root
  for (const p of persons) if (!placed.has(p.id)) placeInRow(p.id);

  // Position nodes
  const nodes = {};
  const sortedGens = Array.from(orderedRows.keys()).sort((a, b) => a - b);
  let maxCols = 0;
  sortedGens.forEach((g) => {
    const row = orderedRows.get(g);
    row.forEach((id, idx) => {
      const person = byId.get(id);
      nodes[id] = {
        ...person,
        gen: g,
        col: idx,
        x: PAD + idx * (NODE_W + COL_GAP),
        y: PAD + (g - sortedGens[0]) * (NODE_H + ROW_GAP),
      };
    });
    maxCols = Math.max(maxCols, row.length);
  });

  const width = PAD * 2 + Math.max(1, maxCols) * (NODE_W + COL_GAP) - COL_GAP;
  const height = PAD * 2 + Math.max(1, sortedGens.length) * (NODE_H + ROW_GAP) - ROW_GAP;

  // Edges for rendering
  const edges = [];
  for (const r of relationships) {
    if (nodes[r.from_person_id] && nodes[r.to_person_id]) {
      edges.push({ ...r });
    }
  }

  return { nodes, edges, size: { w: Math.max(900, width), h: Math.max(500, height) }, NODE_W, NODE_H };
}
